import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import {
  AgCartesianAxisOptions,
  AgCartesianSeriesTooltipRendererParams,
  AgChart,
  AgChartOptions,
  AgLineSeriesOptions,
} from 'ag-charts-community';
import {
  AgChartClickEvent,
  AgChartInstance,
  AgSeriesNodeClickParams,
} from 'ag-charts-community/dist/esm/es6/chart/agChartOptions';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../../base/base.component';
import { Colours } from '../../../constants';
import { BarChartAxisType } from '../../../models';
import { FormatService } from '../../../services';

@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
})
export class LineChartComponent<T> extends BaseComponent implements AfterViewInit {
  @ViewChild('chart') chart: ElementRef<HTMLDivElement>;

  @Input() header: string = '';
  @Input() subHeader: string = '';
  @Input() xKey: string;
  @Input() xAxisName: string;
  @Input() xFormat: BarChartAxisType = BarChartAxisType.text;
  @Input() xRotation: number = 0;

  @Input() yKey: string;
  @Input() yKey2: string;
  @Input() yName: string;
  @Input() yName2: string;
  @Input() yAxisName: string;
  @Input() yFormat: BarChartAxisType = BarChartAxisType.text;
  @Input() yRotation: number = 0;

  @Input() showLegend = true;
  @Input() height: number;
  @Input() data$: Observable<T[]>;
  @Input() subHeaderFunc: (data: T[]) => string;
  @Input() dateFormatFunc: (date: Date) => string;
  @Output() clicked = new EventEmitter<T>();
  @Output() togglePreview = new EventEmitter();

  private agChart: AgChartInstance;
  private options: AgChartOptions;

  constructor(private formatService: FormatService) {
    super();
  }

  ngAfterViewInit(): void {
    this.drawChart();
  }

  formatSummary(count: number, total: number): string {
    return `${this.yName}: ${this.formatValue(count, this.yFormat, true)}, ${this.yName2}: ${this.formatValue(
      total,
      this.yFormat,
      true,
    )}`;
  }

  private drawChart() {
    this.data$.pipe(takeUntil(this.destroy$)).subscribe((data: T[]) => {
      this.getOptions(data);
      if (!this.agChart) {
        this.agChart = AgChart.create(this.options);
      } else {
        AgChart.update(this.agChart, this.options);
      }
    });
  }

  private getOptions(data: T[]) {
    this.options = {
      autoSize: true,
      container: this.chart?.nativeElement,
      height: this.height,
      title: {
        text: this.header,
      },
      subtitle: {
        text: this.subHeaderFunc ? this.subHeaderFunc(data) : this.subHeader,
      },
      data: data,
      series: this.getSeries(),
      axes: this.getAxes(data),
      legend: {
        enabled: this.showLegend,
        position: 'bottom',
      },
      listeners: {
        click: (event: AgChartClickEvent) => this.preview(),
      },
    };
  }

  preview() {
    setTimeout(() => this.togglePreview.emit(), 100);
  }

  private getSeries(): AgLineSeriesOptions[] {
    const series: AgLineSeriesOptions[] = [
      {
        xKey: this.xKey,
        yKey: this.yKey,
        yName: this.yName ?? this.yKey,
        stroke: Colours.blue,
        marker: {
          fill: Colours.blue,
        },
        ...this.seriesDefaults(),
      },
    ];

    if (this.yKey2) {
      series.push({
        xKey: this.xKey,
        yKey: this.yKey2,
        yName: this.yName2 ?? this.yKey2,
        stroke: Colours.red,
        marker: {
          fill: Colours.red,
        },
        ...this.seriesDefaults(),
      });
    }

    return series;
  }

  private seriesDefaults(): AgLineSeriesOptions {
    return {
      type: 'line',
      tooltip: {
        renderer: (params: AgCartesianSeriesTooltipRendererParams) => ({
          title: `${this.xAxisName ? `${this.xAxisName}: ` : ''}${this.formatValue(params.xValue, this.xFormat)}`,
          content: this.formatSummary(params.datum[this.yKey], params.datum[this.yKey2]),
        }),
      },
      listeners: {
        nodeClick: (event: AgSeriesNodeClickParams<any>) => this.clicked.emit(event.datum),
      },
    };
  }

  private getAxes(data: T[]): AgCartesianAxisOptions[] {
    return [
      {
        title: {
          text: this.xAxisName,
        },
        position: 'bottom',
        ...this.axisDefaults(this.xFormat, this.xRotation),
      },
      {
        title: {
          text: this.yAxisName,
        },
        position: 'left',
        keys: [this.yKey, this.yKey2],
        ...this.axisDefaults(this.yFormat, this.yRotation),
      },
    ];
  }

  private axisDefaults(type: BarChartAxisType, rotation: number): any {
    return {
      type: type === BarChartAxisType.number || type === BarChartAxisType.currency ? 'number' : 'category',
      tick: {
        minSpacing: 5,
      },
      label: {
        rotation: rotation,
        formatter: (params) => this.formatValue(params.value, type),
      },
    };
  }

  private formatValue(value: any, type: BarChartAxisType, rounded = true): any {
    switch (type) {
      case BarChartAxisType.number:
        return rounded ? this.formatService.numberShort(value) : this.formatService.number(value);
      case BarChartAxisType.currency:
        return rounded ? this.formatService.currencyShort(value, '£') : this.formatService.currency(value, '£');
      case BarChartAxisType.date:
        return this.dateFormatFunc
          ? this.dateFormatFunc(value)
          : `${this.formatService.dayOfWeekShort(value)} ${this.formatService.date(value)}`;
      default:
        return value;
    }
  }
}
